<template>
  <BundleSignupShell v-if="isBundle">
    <div class="flex justify-center mb-5" style="margin-bottom: 22px">
      <div
        class="inline-flex items-center justify-center gap-2 px-4 py-1.5 rounded-full text-sm font-semibold text-cc-green-600 bg-cc-green-100 border border-cc-green-400"
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="3"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M20 6L9 17l-5-5" />
        </svg>
        Payment confirmed · $299 charged
      </div>
    </div>

    <h1
      class="text-center font-medium text-gray-900"
      style="font-size: 25px; letter-spacing: -0.01em; margin: 0 0 30px"
    >
      Set Your Password
    </h1>
    <div
      class="flex items-start gap-2.5 mx-auto mb-5 px-4 py-3 rounded-xl"
      style="background-color: #f7f8fb; color: #4a5072"
    >
      <svg
        class="w-5 h-5 flex-shrink-0 mt-0.5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      <p class="m-0 text-sm leading-snug text-left">
        Your details carried over from signup — just choose a password to finish.
      </p>
    </div>

    <form @submit.prevent="submitForm" novalidate>
      <div v-if="error" class="text-red-500 mb-2 text-sm text-center">
        {{ error }}
      </div>
      <input type="hidden" v-model="resetPasswordToken" name="reset_password_token" />
      <div class="mb-3">
        <label for="password" class="block text-sm font-semibold text-gray-900 mb-1">
          Password
        </label>
        <input
          id="password"
          type="password"
          v-model="password"
          required
          autofocus
          class="w-full box-border text-sm text-gray-900 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:border-cc-green-500 focus:ring-2 focus:ring-cc-green-500 focus:ring-opacity-20"
          placeholder="Password"
        />
      </div>

      <button
        type="submit"
        class="w-full flex items-center justify-center gap-2 mt-4 px-4 py-2.5 text-base font-semibold text-white bg-cc-green-500 rounded-md hover:brightness-105 transition"
      >
        Sign Up
      </button>

      <p class="text-center text-xs text-gray-600 mt-3 leading-relaxed">
        By joining, you agree to the
        <a
          href="https://coachcatalyst.com/terms-of-use"
          target="_blank"
          class="text-cc-green-500"
          >terms of service</a
        >
        and
        <a
          href="https://coachcatalyst.com/privacy-policy"
          target="_blank"
          class="text-cc-green-500"
          >privacy policy</a
        >
      </p>
    </form>

    <div
      class="flex items-center gap-3 mt-6 px-4 py-3 rounded-xl bg-cc-gc-purple-soft border border-cc-gc-purple"
    >
      <span class="w-2 h-2 rounded-full bg-cc-gc-purple flex-shrink-0"></span>
      <p class="m-0 text-sm leading-snug text-cc-gc-purple-deep">
        <strong class="font-bold">Your Growth Catalyst CRM is being set up.</strong>
        We'll reach out when it's ready.
      </p>
    </div>
  </BundleSignupShell>

  <div
    v-else
    class="flex flex-col items-center justify-center h-screen relative"
    style="z-index: 0"
  >
     <div class="background-image absolute top-0 left-0 w-full h-full">
      <!-- SVG backgrounds copied from login-form.vue -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="absolute top-0 left-0"
        width="25%"
        height="80%"
        viewBox="0 0 364 663"
        fill="none"
      >
        <path
          opacity="0.25"
          :style="[
            requestedOrganization &&
            requestedOrganization.theme &&
            requestedOrganization.theme.primary_color
              ? { color: requestedOrganization.theme.primary_color }
              : { color: '#3AF080' },
          ]"
          d="M267.362 -79.865C440.933 58.1153 418.589 276.134 -125 662.037L-112.459 588.786C80.3826 483.411 543.488 156.388 267.362 -79.865Z"
          fill="#3AF080"
        />
      </svg>
      <svg
        width="25%"
        class="absolute top-0 left-0"
        height="80%"
        viewBox="0 0 359 712"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          opacity="0.25"
          :style="[
            requestedOrganization &&
            requestedOrganization.theme &&
            requestedOrganization.theme.primary_color
              ? { color: requestedOrganization.theme.primary_color }
              : { color: '#3AB373' },
          ]"
          d="M242.205 -54.4781C433.553 54.178 412.512 284.485 -3.71298 711.066L-49.466 657.06C128.403 528.009 546.143 144.727 242.205 -54.4781Z"
          fill="#3AB574"
        />
      </svg>
      <div
        class="absolute top-0 right-0 flex items-start justify-end h-full w-full pointer-events-none"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="25%"
          class="absolute bottom-0 right-0"
          height="80%"
          viewBox="0 0 393 730"
          fill="none"
          preserveAspectRatio="none"
        >
          <path
            opacity="0.25"
            :style="[
              requestedOrganization &&
              requestedOrganization.theme &&
              requestedOrganization.theme.primary_color
                ? { color: requestedOrganization.theme.primary_color }
                : { color: '#3AF080' },
            ]"
            d="M124.182 792.144C-87.2325 652.363 -75.4235 422.517 533.234 0.413395L504.784 274C300.609 271.371 -191.118 542.494 124.182 792.144Z"
            fill="#3AF080"
          />
        </svg>
        <svg
          width="25%"
          class="absolute bottom-0 right-0"
          height="80%"
          viewBox="0 0 351 852"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.25"
            :style="[
              requestedOrganization &&
              requestedOrganization.theme &&
              requestedOrganization.theme.primary_color
                ? { color: requestedOrganization.theme.primary_color }
                : { color: '#3AB373' },
            ]"
            d="M133.691 902C-70.1671 781.302 -85.6564 533.98 408 0V84.0574C227.292 238.342 -193.581 691.318 133.691 902Z"
            fill="#3AB574"
          />
        </svg>
      </div>
    </div>
     <div class="flex flex-col items-center justify-center z-10 gap-4">
        <div v-if="!requestedOrganization">
            <Logo type="ICON_DARK" class="login-logo" />
        </div>
        <div v-else>
            <img
            :src="requestedOrganization.logo_url"
            alt="Coach Catalyst"
            class="login-logo"
            />
        </div>
        <div
            class="w-4 h-0.5 flex-shrink-0 my-4 self-center"
            :style="[
            requestedOrganization &&
            requestedOrganization.theme &&
            requestedOrganization.theme.primary_color
                ? { background: requestedOrganization.theme.primary_color }
                : { background: '#3AB373' },
            ]"
        >
        </div>
        <div v-if="isBundle" class="paid-pill flex items-center justify-center gap-2 mb-2 px-4 py-1.5 rounded-full text-sm font-semibold text-cc-green-600 bg-cc-green-100 border border-cc-green-400">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
          Payment confirmed · $299 charged
        </div>
        <h2 class="login-p">{{ isBundle ? 'Set Your Password' : 'Change your password' }}</h2>

        <p v-if="isBundle" class="text-sm text-gray-500 text-center max-w-sm">
          Your details carried over from signup — just choose a password to finish.
        </p>

        <form
            class="simple_form new_user w-full gap-4"
            id="new_user"
            @submit.prevent="submitForm"
            novalidate
            >
        <div v-if="error" class="text-red-500 mb-2">{{ error }}</div>
        <input
            type="hidden"
            v-model="resetPasswordToken"
            name="reset_password_token"
        />
        <div class="form-inputs flex flex-col gap-4">
            <label for="password" class="font-medium">{{ isBundle ? 'Password' : 'New password' }}</label>
            <input
            id="password"
            type="password"
            v-model="password"
            required
            autofocus
            class="input"
            :placeholder="isBundle ? 'Password' : 'New password'"
            />

            <template v-if="!isBundle">
            <label for="password_confirmation" class="font-medium">Confirm your new password</label>
            <input
            id="password_confirmation"
            type="password"
            v-model="passwordConfirmation"
            required
            class="input"
            placeholder="Confirm your new password"
            />
            </template>
        </div>
        <div class="form-actions mt-4">
            <button type="submit" class="btn button submit-button">
            {{ isBundle ? 'Sign Up' : 'Change my password' }}
            </button>
        </div>
        </form>

        <div v-if="isBundle" class="webhook-note flex items-center gap-3 mt-6 px-4 py-3 rounded-xl bg-cc-gc-purple-soft border border-cc-gc-purple">
          <span class="w-2 h-2 rounded-full bg-cc-gc-purple flex-shrink-0"></span>
          <p class="m-0 text-sm leading-snug text-cc-gc-purple-deep">
            <strong class="font-bold">Your Growth Catalyst CRM is being set up.</strong> We'll reach out when it's ready.
          </p>
        </div>

        <template v-if="!isBundle">
        <div class="w-4 h-0.5 flex-shrink-0 my-4 self-center bg-gray-500"></div>
            <div class="self-stretch h-5 text-center justify-start">
              <div class="flex text-center items-center justify-center">
                  <a href="/auth/sign_in">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                    </svg>
                  </a>

                  <span class="text-base font-normal font-['Inter'] mr-2">Back to </span>
                  <a href="/auth/sign_in" class="text-base font-semibold font-['Inter'] cursor-pointer" :style="[
                    requestedOrganization &&
                    requestedOrganization.theme &&
                    requestedOrganization.theme.primary_color
                      ? { color: requestedOrganization.theme.primary_color }
                      : { color: '#3AB373' },
                  ]">Sign in</a>

              </div>
        </div>
        </template>
       
    </div>
  </div>
</template>

<script>
import ApiUrls from '../api-url';
import AuthenticationMixin from '../authentication-mixin';
import { mapGetters, mapState } from 'vuex';
import Logo from '../components/logos/Logo.vue';
import { trackEvent } from '../services/analytics';
import BundleSignupShell from './signup/bundle/bundle-signup-shell.vue';

export default {
  mixins: [ApiUrls, AuthenticationMixin],
  components: {
    Logo,
    BundleSignupShell,
  },
  data() {
    return {
      resetPasswordToken: '', // Set this from query params or props
      password: '',
      passwordConfirmation: '',
      isBundle: false,
    };
  },
  created() {
    // Example: get token from query string
    this.resetPasswordToken = this.$route.query.reset_password_token || '';
    this.isBundle = this.$route.query.bundle === '1';
    document.title = this.isBundle
      ? 'Set Your Password | Coach Catalyst'
      : 'Change Password | Coach Catalyst';

    this.getRequestedOrganization();
  },
  computed: {
    ...mapState(['requestedOrganization']),
    ...mapGetters([]),
  },
  methods: {
    async submitForm() {
      const confirmation = this.isBundle ? this.password : this.passwordConfirmation;

      if (!this.password || !confirmation) {
        this.$toast.show('Please fill in all fields.', this.toasted_options.error );
        return;
      }
      if (this.password !== confirmation) {
        this.$toast.show('Passwords do not match.', this.toasted_options.error );
        return;
      }

      const formData = new FormData();
      formData.append('utf8', '✓');
      formData.append('_method', 'put');
      const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
      formData.append('authenticity_token', csrfToken);
      formData.append('user[reset_password_token]', this.resetPasswordToken);
      formData.append('user[password]', this.password);
      formData.append('user[password_confirmation]', confirmation);
      formData.append('commit', 'Change my password');

      try {
        const response = await this.axiosInstance.post('/auth/password', formData, {
          headers: { 'X-CSRF-Token': csrfToken }
        });
        if (response.status === 200) {
          if (this.isBundle) {
            this.$toast.show('Password set. Welcome aboard!', this.toasted_options.success );
            trackEvent('bundle_signup_password_set', {});
            // Continue into the standard Coach Catalyst onboarding.
            window.location.href = '/auth/complete_signup';
          } else {
            this.$toast.show('Password changed successfully.', this.toasted_options.success );
            window.location.href = '/';
          }
        } else {
          this.$toast.show('Password change failed.', this.toasted_options.error );
        }
      } catch (err) {
        this.$toast.show(
          err?.response?.data?.error || 'Failed to change password.',
          this.toasted_options.error
        );
      }
    },

    async getRequestedOrganization() {
      const org_subdomain = window.location.host.split('.')[0];
      const { data } = await this.axiosInstance.post(`${this.organizationUrl}requested_organization`, { org_subdomain });
      if (data.success) {
        await this.$store.commit('setRequestedOrganization', data.result);
      }
    },
  },
};
</script>

<style lang="scss"></style>